import { createChallenge, verifySignature } from 'api/copyBotApi';
import { Address } from 'viem';
import { useSignMessage } from 'wagmi';

type StoredToken = {
  token: string;
  expiresAt: string;
};

const STORAGE_KEY = 'copy-bot-auth-tokens';

const readTokens = (): Record<string, StoredToken> => {
  if (typeof window === 'undefined') return {};

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, StoredToken>) : {};
  } catch {
    return {};
  }
};

const writeTokens = (tokens: Record<string, StoredToken>) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
};

const getStoredToken = (owner: Address) => {
  const entry = readTokens()[owner.toLowerCase()];
  if (!entry) return null;
  if (new Date(entry.expiresAt).getTime() <= Date.now()) return null;
  return entry.token;
};

const setStoredToken = (owner: Address, token: string, expiresAt: string) => {
  const key = owner.toLowerCase();
  const tokens = readTokens();
  tokens[key] = { token, expiresAt };
  writeTokens(tokens);
};

export const clearStoredCopyBotToken = (owner?: Address) => {
  if (typeof window === 'undefined') return;
  if (!owner) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  const tokens = readTokens();
  delete tokens[owner.toLowerCase()];
  writeTokens(tokens);
};

export const useCopyBotAuth = () => {
  const { signMessageAsync } = useSignMessage();

  const getAccessToken = async (owner: Address) => {
    const cachedToken = getStoredToken(owner);
    if (cachedToken) return cachedToken;

    const challenge = await createChallenge({ owner });
    const signature = await signMessageAsync({ message: challenge.message });
    const verified = await verifySignature({
      owner,
      nonce: challenge.nonce,
      signature,
    });

    setStoredToken(owner, verified.token, verified.expiresAt);
    return verified.token;
  };

  return {
    getAccessToken,
    clearAccessToken: clearStoredCopyBotToken,
  };
};
